import React, { useState, useEffect } from "react";
import { Button } from "@shared/ui/button";
import {
  Users,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash,
  Pencil,
  BadgeCheck,
  InfoIcon,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";

import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useConfigMapData,
  usePostConfigMap,
  usePatchConfigMap,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@amplitude/analytics-browser";

export interface Category {
  name: string;
  isActive: boolean;
  createdBy: string;
  description: string;
  createdDate: string;
  code: string;
  childCount: number;
  hierarchyNames: {
    category: string;
  };
  hierarchyCodes: {
    category: string;
  };
}

export interface SubCategory {
  name: string;
  isActive: boolean;
  createdBy: string;
  description: string;
  createdDate: string;
  code: string;
  childCount: number;
  hierarchyNames: {
    category: string;
    subCategory: string;
  };
  hierarchyCodes: {
    category: string;
    subCategory: string;
  };
}

interface CategoryItemProps {
  item: Category;
  onEdit: () => void;
  onDelete: () => void;
  subCategories: SubCategory[];
  onAddSubCategory: (parentCode: string) => void;
  onEditSubCategory: (subCategory: SubCategory) => void;
  onDeleteSubCategory: (subCategory: SubCategory) => void;
  isLoadingSubCategories: boolean;
  onToggleExpand: (categoryCode: string, isOpen: boolean) => void;
  isExpanded: boolean;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
  item,
  onEdit,
  onDelete,
  subCategories = [],
  onAddSubCategory,
  onEditSubCategory,
  onDeleteSubCategory,
  isLoadingSubCategories,
  onToggleExpand,
  isExpanded,
}) => {
  const handleToggle = () => {
    onToggleExpand(item.code, !isExpanded);
  };

  return (
    <div className="rounded-md border mb-3 transition-colors overflow-hidden">
      <div className="flex items-center justify-between p-3 hover:bg-muted/30">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base">{item.name}</h3>

            {item.childCount > 0 && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {item.childCount} sub-categories
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{item.description}</p>
          <p className="text-xs text-muted-foreground">
            Created by {item.createdBy} on{" "}
            {new Date(item.createdDate).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={handleToggle}>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </div>

      <Collapsible open={isExpanded} onOpenChange={handleToggle}>
        <CollapsibleContent>
          <div className="bg-muted/20 p-3 border-t">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium">Sub-Categories</h4>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onAddSubCategory(item.code)}
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>

            {isLoadingSubCategories ? (
              <div className="text-xs text-muted-foreground">
                Loading sub-categories...
              </div>
            ) : subCategories.length > 0 ? (
              <div className="space-y-1">
                {subCategories.map((sub) => (
                  <div
                    key={sub.code}
                    className="flex justify-between items-center p-2 bg-background rounded text-sm"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span>{sub.name}</span>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => onEditSubCategory(sub)}
                      >
                        <Pencil className="h-3 w-3 text-muted-foreground hover:text-primary" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No sub-categories defined
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const ConsumerCategoryComponent: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isAddingSubCategory, setIsAddingSubCategory] = useState(false);
  const [isEditingSubCategory, setIsEditingSubCategory] = useState(false);
  const [editingSubCategory, setEditingSubCategory] =
    useState<SubCategory | null>(null);
  const [subCategoryParentCode, setSubCategoryParentCode] = useState<
    string | null
  >(null);

  // SIMPLIFIED: Only track which single category is expanded
  const [expandedCategoryCode, setExpandedCategoryCode] = useState<
    string | null
  >(null);

  // Fetch categories
  const {
    data: categories,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useConfigMapData<any>(remoteUtilityId, "CATEGORY_JSON", "category");

  // Get the hierarchy code for the expanded category
  const activeSubCategoryCode = expandedCategoryCode
    ? categories?.result?.find((cat) => cat.code === expandedCategoryCode)
        ?.hierarchyCodes?.category || null
    : null;

  // Fetch sub-categories for the expanded category only
  const {
    data: subCategories = [],
    isLoading: isLoadingSubCategories,
    refetch: refetchSubCategories,
  } = useConfigMapData<SubCategory[]>(
    remoteUtilityId,
    "CATEGORY_JSON",
    "sub_category",
    activeSubCategoryCode || undefined // Only fetch when we have an expanded category
  );

  // Mutation hooks
  const createUpdateMutation = usePostConfigMap();
  const patchMutation = usePatchConfigMap();

  // Form fields for category
  const categoryFormFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Name",
      required: true,
      placeholder: "e.g. Residential",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },

    {
      name: "description",
      type: "text",
      label: "Description",
      required: false,
      placeholder: "e.g. Individual home consumers",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Form fields for sub-category
  const subCategoryFormFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Name",
      required: true,
      placeholder: "e.g. Single Family",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Create form instances
  const categoryForm = FormService.createForm({
    fields: categoryFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const subCategoryForm = FormService.createForm({
    fields: subCategoryFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleCategorySubmit = async (data: {
    name: string;
    code: string;
    description: string;
  }) => {
    try {
      if (editingCategory) {
        await patchMutation.mutateAsync(
          {
            remote_utility_id: remoteUtilityId,
            mapType: "CATEGORY_JSON",
            code: editingCategory.hierarchyCodes.category,
            name: data.name,
            description: data.description,
          },
          {
            onSuccess: () => {
              logEvent("System Settings Category Modified", {
                remote_utility_id: remoteUtilityId,
                code: editingCategory.hierarchyCodes.category,
                name: data.name,
              });
            },
          }
        );
      } else {
        await createUpdateMutation.mutateAsync(
          {
            remote_utility_id: remoteUtilityId,
            mapType: "CATEGORY_JSON",
            code: data.name,
            name: data.name,
            description: data.description,
          },
          {
            onSuccess: () => {
              logEvent("System Settings Category Added", {
                remote_utility_id: remoteUtilityId,
                code: data.name,
                name: data.name,
              });
            },
          }
        );
      }

      categoryForm.reset();
      setIsAddingCategory(false);
      setEditingCategory(null);
      refetchCategories();
    } catch (error) {
      console.error("Category operation failed:", error);
    }
  };

  const handleSubCategorySubmit = async (data: { name: string }) => {
    try {
      if (isEditingSubCategory && editingSubCategory) {
        await patchMutation.mutateAsync(
          {
            remote_utility_id: remoteUtilityId,
            mapType: "CATEGORY_JSON",
            code: editingSubCategory.hierarchyCodes.subCategory,
            name: data.name,
          },
          {
            onSuccess: () => {
              logEvent("System Settings Sub Category Modified", {
                remote_utility_id: remoteUtilityId,
                code: editingSubCategory.hierarchyCodes.subCategory,
                name: data.name,
              });
            },
          }
        );
        setIsEditingSubCategory(false);
        setEditingSubCategory(null);
      } else if (subCategoryParentCode) {
        const parentCategory = categories.result.find(
          (cat) => cat.code === subCategoryParentCode
        );
        if (parentCategory) {
          const finalCode = `${parentCategory.hierarchyCodes.category}_${data.name}`;

          await createUpdateMutation.mutateAsync(
            {
              remote_utility_id: remoteUtilityId,
              mapType: "CATEGORY_JSON",
              name: data.name,
              code: finalCode,
            },
            {
              onSuccess: () => {
                logEvent("System Settings Sub Category Added", {
                  remote_utility_id: remoteUtilityId,
                  code: data.name,
                  name: finalCode,
                });
              },
            }
          );
        }
      }

      subCategoryForm.reset();
      setIsAddingSubCategory(false);
      setSubCategoryParentCode(null);
      refetchSubCategories();
    } catch (error) {
      console.error("Sub-category operation failed:", error);
    }
  };

  // Event handlers
  const startAddCategory = () => {
    categoryForm.reset();
    setEditingCategory(null);
    setIsAddingCategory(true);
  };

  const editCategory = (category: Category) => {
    setEditingCategory(category);
    categoryForm.setValue("name", category.name);
    categoryForm.setValue("code", category.code);
    categoryForm.setValue("description", category.description);
    setIsAddingCategory(true);
  };

  const deleteCategory = async (category: Category) => {
    try {
      // TODO: Implement API call to delete category
      refetchCategories();
    } catch (error) {
      console.error("Failed to delete category:", error);
    }
  };

  const startAddSubCategory = (parentCode: string) => {
    setSubCategoryParentCode(parentCode);
    setIsEditingSubCategory(false);
    setEditingSubCategory(null);
    setIsAddingSubCategory(true);
    subCategoryForm.reset();
  };

  const editSubCategory = (subCategory: SubCategory) => {
    setIsEditingSubCategory(true);
    setEditingSubCategory(subCategory);

    // Find the parent category code from the subcategory's hierarchy
    const parentCategoryCode = categories.result.find(
      (cat) =>
        cat.hierarchyCodes.category === subCategory.hierarchyCodes.category
    )?.code;

    setSubCategoryParentCode(parentCategoryCode || null);
    subCategoryForm.setValue("name", subCategory.name);
    setIsAddingSubCategory(true);
  };

  const deleteSubCategory = async (subCategory: SubCategory) => {
    try {
      // TODO: Implement API call to delete sub-category
      refetchSubCategories();
    } catch (error) {
      console.error("Failed to delete sub-category:", error);
    }
  };

  const cancelCategoryEdit = () => {
    setIsAddingCategory(false);
    setEditingCategory(null);
    categoryForm.reset();
  };

  const cancelSubCategoryAdd = () => {
    setIsAddingSubCategory(false);
    setIsEditingSubCategory(false);
    setEditingSubCategory(null);
    setSubCategoryParentCode(null);
    subCategoryForm.reset();
  };

  // SIMPLIFIED: Handle category expansion (only one at a time)
  const handleCategoryToggle = (categoryCode: string, isOpen: boolean) => {
    if (isOpen) {
      // Open this category (and close any other)
      setExpandedCategoryCode(categoryCode);
    } else {
      // Close this category
      setExpandedCategoryCode(null);
    }
  };

  // Get sub-categories for a specific category
  const getSubCategoriesForCategory = (categoryCode: string): SubCategory[] => {
    // Only return subcategories if this is the currently expanded category
    if (expandedCategoryCode === categoryCode) {
      if (Array.isArray(subCategories)) {
        return subCategories;
      }
      // Handle paginated response
      if (subCategories && Array.isArray((subCategories as any).result)) {
        return (subCategories as any).result;
      }
    }

    return [];
  };

  // Check if loading subcategories for a specific category
  const isLoadingSubCategoriesForCategory = (categoryCode: string): boolean => {
    return expandedCategoryCode === categoryCode && isLoadingSubCategories;
  };

  if (isLoadingCategories) {
    return (
      <div className="mb-8 border rounded-md">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
           
            <h3 className="font-medium">Consumer Categories</h3>
          </div>
        </div>
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8  border rounded-md p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex flex-col ">
         
          <h3 className="card-heading-text">Consumer Categories</h3>
          <p className="card-subheading-text">Select and manage categories for classifying consumers.</p>
        </div>
        {!isAddingCategory && !isAddingSubCategory && (
          <Button size="sm"  onClick={startAddCategory}>
            <Plus className="h-4 w-4 mr-1" /> Add Category
          </Button>
        )}
      </div>

      <Accordion type="single" collapsible className="mb-4">
        <AccordionItem value="help">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">Need help with Categories?</div>
          </AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground">
            <p className="mb-2">
              Define and manage consumer categories in SMART360:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Add categories to group consumers (e.g., Residential,
                Commercial, Industrial).
              </li>
              <li>Set category-specific billing rules or tariffs.</li>
              <li>Use categories for reporting and service management.</li>
              <li>Ensure each consumer is assigned to the correct category.</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {isAddingCategory ? (
        <div className="border rounded-md p-4 mb-4">
          <h3 className="font-medium mb-4">
            {editingCategory
              ? "Edit Consumer Category"
              : "Add New Consumer Category"}
          </h3>
          <DynamicForm
            fields={categoryFormFields}
            form={categoryForm}
            onSubmit={handleCategorySubmit}
            onReset={cancelCategoryEdit}
            loading={createUpdateMutation.isPending || patchMutation.isPending}
            config={{
              showProgress: false,
              layout: "vertical",
              submitButtonText: editingCategory ? "Update" : "Add",
              showResetButton: true,
              resetButtonText: "Cancel",
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                fieldContainer: "",
                buttonContainer: "flex justify-end space-x-2 pt-4",
                fieldGroup: "",
              },
            }}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
      ) : isAddingSubCategory ? (
        <div className="border rounded-md p-4 mb-4">
          <h3 className="font-medium mb-4">
            {isEditingSubCategory
              ? "Edit Consumer Sub-Category"
              : "Add New Consumer Sub-Category"}
          </h3>
          <DynamicForm
            fields={subCategoryFormFields}
            form={subCategoryForm}
            onSubmit={handleSubCategorySubmit}
            onReset={cancelSubCategoryAdd}
            loading={createUpdateMutation.isPending || patchMutation.isPending}
            config={{
              showProgress: false,
              layout: "vertical",
              submitButtonText: isEditingSubCategory ? "Update" : "Add",
              showResetButton: true,
              resetButtonText: "Cancel",
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                fieldContainer: "",
                buttonContainer: "flex justify-end space-x-2 pt-4",
                fieldGroup: "",
              },
            }}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
      ) : categories?.result.length > 0 ? (
        <div>
          {categories?.result?.map((category) => (
            <CategoryItem
              key={category.code}
              item={category}
              onEdit={() => editCategory(category)}
              onDelete={() => deleteCategory(category)}
              subCategories={getSubCategoriesForCategory(category.code)}
              onAddSubCategory={startAddSubCategory}
              onEditSubCategory={editSubCategory}
              onDeleteSubCategory={deleteSubCategory}
              isLoadingSubCategories={isLoadingSubCategoriesForCategory(
                category.code
              )}
              onToggleExpand={handleCategoryToggle}
              isExpanded={expandedCategoryCode === category.code}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 border rounded-md bg-muted/30">
          <BadgeCheck className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          <h3 className="font-medium mb-1">No Consumer Categories</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            Create categories to classify your customers for different billing
            rates and service options.
          </p>
          <Button onClick={startAddCategory}>
            <Plus className="h-4 w-4 mr-1" /> Add Your First Category
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConsumerCategoryComponent;
